<template>
  <div class="appContainer" v-bind:class="[routeName, settings.orgType]">
    <Debug />
    <header>
      <NavigationC></NavigationC>
    </header>
    <ServerStatus v-if="serverStatus !== 'ok' && !offline" />
    <Notifications v-if="isLoggedin && appReady && $route.name != null"></Notifications>

    <smoothReflow>
      <div class="breadcrumbsAndTools">
        <BreadcrumbsC v-if="isLoggedin && appReady && $route.name != null" />
        <ToolBar v-if="isLoggedin && appReady"></ToolBar>
      </div>
    </smoothReflow>
    <LoadingC v-if="!appReady"></LoadingC>
    <main v-if="appReady">
      <router-view v-slot="{ Component }" :key="$route.name">
        <component :is="Component" />
        <!--transition name="fade">
          <component :is="Component" />
        </transition-->
      </router-view>
    </main>
    <smoothReflow>
      <footer>
        <FooterEl></FooterEl>
      </footer>
    </smoothReflow>
  </div>
</template>

<script>
import NavigationC from './components/Navigation/NavigationC.vue'
import BreadcrumbsC from './components/Navigation/BreadcrumbsC.vue'
import Notifications from './components/Notifications/Notifications.vue'
import ServerStatus from './components/ServerStatus/ServerStatus.vue'
import FooterEl from './components/Footer/Footer.vue'
import LoadingC from './components/LoadingC.vue'
import Debug from './components/Debug/Debug.vue'
import ToolBar from './components/ToolBar/ToolBar.vue'
import { mapGetters, mapState } from 'vuex'
import { orgTerms } from '@/terminology'

export default {
  name: 'App',
  components: {
    NavigationC,
    FooterEl,
    BreadcrumbsC,
    Notifications,
    ServerStatus,
    LoadingC,
    Debug,
    ToolBar,
  },
  data() {
    return {}
  },
  computed: {
    ...mapState({
      appReady: (state) => state.appReady,
      serverStatus: (state) => state.serverStatus,
      offline: (state) => state.offline,
    }),
    ...mapGetters({
      isLoggedin: 'auth/isLoggedin',
      isAdminOrEditor: 'auth/isAdminOrEditor',
      settings: 'settings/settings',
    }),
    routeName() {
      if (typeof this.$route.matched[0] !== 'undefined') {
        if (typeof this.$route.matched[0].name !== 'undefined') {
          return 'c-' + this.$route.matched[0].name
        }
      }
      return ''
    },
  },
  watch: {
    isLoggedin(loggedIn) {
      // Hydrate pronobis import status as soon as an admin/editor logs in,
      // so the banner appears without requiring a full page refresh. The
      // endpoint is admin/editor-gated; calling it as a regular member is
      // a guaranteed 401.
      if (
        loggedIn &&
        this.isAdminOrEditor &&
        import.meta.env.VITE_CLIENT === 'pronobis'
      ) {
        this.$store.dispatch('pronobisImportStatus/fetch')
      }
    },
  },
  mounted() {
    // #389 — this tested `import.meta.env.NODE_ENV === 'development'`, which Vite
    // never defines, so it read `undefined === 'development'` and this hook had
    // never executed in any environment. Same root cause as the LoginC credential
    // guard, failing the other way: that one was always on, this one always off.
    //
    // `import.meta.env.DEV` is correct *here*, unlike in LoginC: it is true under
    // the dev server and vitest and false in `vite build`, so the toasts stay out
    // of production and out of the Playwright fleet's `--mode dev` build, where
    // popping a toast on every console.error could interfere with the specs.
    if (import.meta.env.DEV) {
      ;['error'].forEach((verb) => {
        console[verb] = (function (method, verb) {
          return function () {
            method.apply(console, arguments)
            const msg = `${Array.prototype.slice.call(arguments).join(' ')}`
            // Optional-chained because this now actually runs: App's mounted()
            // fires *during* app.mount(), before main.js assigns window.vue, so a
            // console.error raised while the app is still booting would otherwise
            // throw a TypeError over the top of the error being reported.
            window.vue?.$toasted?.[verb](msg)?.goAway(3000)
          }
        })(console[verb], verb)
      })
    }

    const promises = []
    if (
      !(
        (window.location.pathname.startsWith('/v/') ||
          window.location.pathname.startsWith('/inscricao/confirmacao')) &&
        !this.isLoggedin
      )
    ) {
      promises.push(this.$store.dispatch('settings/list'))
      promises.push(this.$store.dispatch('explainer/load'))

      Promise.all(promises)
        .then(() => {
          this.$store.dispatch('appReady', true)

          const filesDir = this.settings.filesDir
          document.querySelector('#fimdomeioFavicon').href =
            this.$apiURL + '/' + filesDir + '/favicon.png'

          window.document.title = orgTerms.name

          // Pronobis-only: hydrate the import status so the banner can
          // show on page load without waiting for a fresh sync. Gated on
          // admin/editor because the endpoint is role-gated; calling it
          // as an unauthenticated or regular user is wasted work (and on
          // the login page it would loop the refresh-token interceptor).
          if (
            import.meta.env.VITE_CLIENT === 'pronobis' &&
            this.isLoggedin &&
            this.isAdminOrEditor
          ) {
            this.$store.dispatch('pronobisImportStatus/fetch')
          }
        })
        .catch((err) => {
          console.error(err)
        })
    } else {
      this.$store.dispatch('appReady', true)
    }
  },
}
</script>

<style lang="scss">
@use '@/scss/general.scss';
</style>

<style scoped lang="scss">
@use '@/scss/colors/_sass-material-colors-function.scss';
.appContainer {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

header {
  position: sticky;
  top: -1px;
  z-index: 3;
}
footer {
  margin-top: calc(var(--sp) * 3);
}
header #nav,
footer {
  background-color: var(--header-background);
  border-bottom: 1px solid var(--header-background-darker);
  box-shadow: var(--default-shadow);
}
main {
  margin: calc(var(--sp) * 1) calc(var(--sp) * 1);
  flex-grow: 1;
  @media print {
    margin: calc(var(--sp) * 0) calc(var(--sp) * 1);
  }
}
@media only screen and (max-width: 700px) {
  main {
    margin: var(--sp);
  }
}

.breadcrumbsAndTools {
  display: flex;
  justify-content: space-between;
}
@media print {
  .breadcrumbsAndTools {
    display: none;
  }
  footer {
    display: none;
  }
}
.fade-enter-from,
.fade-leave-to {
  position: absolute;
  width: 100%;
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s linear;
}
</style>
